import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  getCampaigns,
  startCampaign,
  pauseCampaign,
  archiveCampaign,
} from '@/lib/campaigns';
import type { Campaign } from '@/types/campaigns';
import { Plus, Play, Pause, RefreshCw, Archive } from 'lucide-react';

function getStatusBadgeVariant(status: string) {
  switch (status) {
    case 'running':
      return 'success';
    case 'completed':
      return 'secondary';
    case 'paused':
      return 'warning';
    default:
      return 'outline';
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const loadCampaigns = useCallback(async (includeArchived: boolean = showArchived) => {
    try {
      setLoading(true);
      setError('');
      const data = await getCampaigns(undefined, includeArchived);
      setCampaigns(data);
    } catch (err) {
      setError('Failed to load campaigns');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    loadCampaigns(showArchived);
  }, [loadCampaigns, showArchived]);

  const handleStart = async (campaignId: string) => {
    try {
      setActionLoading(campaignId);
      await startCampaign(campaignId);
      await loadCampaigns(showArchived);
    } catch (err) {
      console.error('Failed to start campaign:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async (campaignId: string) => {
    try {
      setActionLoading(campaignId);
      await pauseCampaign(campaignId);
      await loadCampaigns(showArchived);
    } catch (err) {
      console.error('Failed to pause campaign:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (campaignId: string) => {
    try {
      setActionLoading(campaignId);
      await archiveCampaign(campaignId);
      await loadCampaigns(showArchived);
    } catch (err) {
      console.error('Failed to archive campaign:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Campaigns</h1>
          <p className="text-muted-foreground">
            Manage your outbound calling campaigns
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild>
            <Link to="/campaigns/new">
              <Plus className="mr-2 h-4 w-4" />
              New Campaign
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => loadCampaigns(showArchived)}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Show archived toggle */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="showArchived"
          checked={showArchived}
          onCheckedChange={(checked) => setShowArchived(checked === true)}
        />
        <Label
          htmlFor="showArchived"
          className="cursor-pointer text-sm text-muted-foreground"
        >
          Show archived campaigns
        </Label>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="py-4">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="mb-4 text-muted-foreground">No campaigns yet</p>
            <Button asChild>
              <Link to="/campaigns/new">
                <Plus className="mr-2 h-4 w-4" />
                Create your first campaign
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Campaigns</CardTitle>
            <CardDescription>
              {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Results</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => {
                  const progress =
                    campaign.total > 0
                      ? Math.round((campaign.completed / campaign.total) * 100)
                      : 0;

                  return (
                    <TableRow
                      key={campaign.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    >
                      <TableCell className="font-medium">
                        {campaign.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{campaign.type}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge
                            variant={getStatusBadgeVariant(campaign.status)}
                          >
                            {campaign.status}
                          </Badge>
                          {campaign.is_archived && (
                            <Badge
                              variant="outline"
                              className="text-muted-foreground"
                            >
                              archived
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex min-w-[120px] items-center gap-2">
                          <Progress value={progress} className="h-2 flex-1" />
                          <span className="w-16 text-sm text-muted-foreground">
                            {campaign.completed}/{campaign.total}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span
                            className={
                              campaign.answered > 0
                                ? 'text-green-600'
                                : 'text-muted-foreground'
                            }
                          >
                            {campaign.answered} answered
                          </span>
                          {campaign.no_answer > 0 && (
                            <span className="text-muted-foreground">
                              {' '}
                              · {campaign.no_answer} no answer
                            </span>
                          )}
                          {campaign.failed > 0 && (
                            <span className="text-destructive">
                              {' '}
                              · {campaign.failed} failed
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(campaign.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {campaign.status === 'pending' ||
                          campaign.status === 'paused' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStart(campaign.id);
                              }}
                              disabled={actionLoading === campaign.id}
                              title="Start campaign"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          ) : campaign.status === 'running' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePause(campaign.id);
                              }}
                              disabled={actionLoading === campaign.id}
                              title="Pause campaign"
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {/* Archive button - only for non-running, non-archived campaigns */}
                          {campaign.status !== 'running' &&
                            !campaign.is_archived && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchive(campaign.id);
                                }}
                                disabled={actionLoading === campaign.id}
                                title="Archive campaign"
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
